import '@polymer/polymer/polymer-legacy.js';
import '@polymer/paper-dialog/paper-dialog.js';
import { KatapultGeometry } from 'katapult-toolbox';
import { SquashNulls } from '../../modules/SquashNulls.js';
import { DataLayer } from '../../modules/DataLayer/DataLayer.js';
import { InsertJobReferenceConnection } from '../../modules/InsertJobReferenceConnection.js';
import { Polymer } from '@polymer/polymer/lib/legacy/polymer-fn.js';
import { html } from '@polymer/polymer/lib/utils/html-tag.js';
import { PickAnAttribute } from '../../modules/PickAnAttribute.js';
import { Round } from '../../modules/Round.js';
import '../katapult-elements/katapult-button.js';
import { Throttle } from '../../modules/Throttle.js';
import { Convert } from '../../modules/Convert.js';

/* global k, Polymer, firebase, google, FirebaseWorker */
Polymer({
  _template: html`
    <style include="paper-dialog-style">
      [hidden] {
        display: none !important;
      }
      #drawingGeometry[invisible] {
        pointer-events: none;
        opacity: 0;
        margin-top: var(--sl-spacing-small);
      }
      #drawingGeometry {
        position: absolute;
        top: 4em;
        left: 1em;
        width: 120px;
        padding: var(--sl-spacing-x-small);
        box-sizing: border-box;
        z-index: 2;
        display: flex;
        flex-direction: column;
        gap: var(--sl-spacing-2x-small);
        background-color: var(--sl-color-neutral-0);
        border-radius: var(--sl-border-radius-large);
        box-shadow: var(--sl-shadow-medium);
        opacity: 1;
        transition:
          margin 0.3s,
          opacity 0.3s;
      }
      #drawingGeometry paper-input [slot='suffix'] {
        font-size: var(--sl-font-size-small);
      }
      .drawingGeometryInputRow {
        display: flex;
      }
      .drawingGeometryLockIcon {
        padding-right: 0;
        padding-left: 2px;
      }
    </style>
    <div id="drawingGeometry" invisible$="[[!showDrawingGeometry]]">
      <div class="drawingGeometryInputRow" hidden$="[[!showDrawingGeometryInputs]]">
        <paper-input
          id="inputLength"
          value="{{_snappingLength}}"
          on-focus="lockGeometry"
          no-label-float=""
          type="number"
          inputmode="decimal"
        >
          <template is="dom-if" if="[[useMetricUnits]]">
            <span slot="suffix">m</span>
          </template>
          <template is="dom-if" if="[[!useMetricUnits]]">
            <span slot="suffix">ft</span>
          </template>
        </paper-input>
        <katapult-collapse horizontal opened="[[lockedGeometryLength]]">
          <katapult-button
            id="lengthLock"
            iconOnly
            noBorder
            class="drawingGeometryLockIcon"
            icon="lock"
            on-click="unlockGeometry"
          ></katapult-button>
        </katapult-collapse>
      </div>
      <div class="drawingGeometryInputRow" hidden$="[[!showDrawingGeometryInputs]]">
        <paper-input id="inputAngle" value="{{_snappingAngle}}" on-focus="lockGeometry" no-label-float="" type="number" inputmode="decimal">
          <span slot="suffix">deg</span>
        </paper-input>
        <katapult-collapse horizontal opened="[[lockedGeometryAngle]]">
          <katapult-button
            id="angleLock"
            iconOnly
            noBorder
            class="drawingGeometryLockIcon"
            icon="lock"
            on-click="unlockGeometry"
          ></katapult-button>
        </katapult-collapse>
      </div>
      <div style="display: flex; justify-content: space-around;" hidden$="[[!showDrawingGeometryInputs]]">
        <katapult-button
          iconOnly
          noBorder
          color="[[_bisectorToggleButtonColor]]"
          id="bisectorIcon"
          icon="call_merge"
          on-click="toggleBisectorInline"
        ></katapult-button>
        <katapult-button
          iconOnly
          noBorder
          color="[[_inlineToggleButtonColor]]"
          id="inlineIcon"
          icon="east"
          on-click="toggleBisectorInline"
        ></katapult-button>
      </div>
      <div style="display:flex; flex-direction: column; align-items: center; gap: var(--sl-spacing-x-small);">
        <katapult-button
          iconOnly
          noBorder
          icon="gps_fixed"
          on-click="showOrHideGPSActions"
          disabled="[[!showDrawingGeometryInputs]]"
        ></katapult-button>
        <template is="dom-if" if="[[gpsCircleConfig.visible]]">
          <div style="max-width: 80px; font-size: var(--sl-font-size-x-small); font-style: italic; text-align: center;">
            (GPS position accurate to within [[formatGpsAccuracy(gpsCircleConfig.accuracy, useMetricUnits)]])
          </div>
        </template>
      </div>
    </div>
  `,

  /*
    fires toast, prompt, cancel-prompt, secondary-prompt, cancel-secondary-prompt zoom-to-active
  */
  is: 'draw-on-map',

  properties: {
    activeItem: {
      type: String,
      notify: true,
      observer: 'activeItemChanged'
    },
    activeCommand: {
      type: String,
      notify: true,
      observer: 'activeCommandChanged'
    },
    activeButton: {
      type: String,
      notify: true,
      observer: 'activeButtonChanged'
    },
    activeButtonModel: {
      type: Object
    },
    activeTool: {
      type: String,
      notify: true
    },
    bisectorBearings: {
      type: Array,
      value: function () {
        return [];
      }
    },
    bisectorIndex: {
      type: Number,
      value: 0
    },
    collectionOrderLogic: {
      type: Object
    },
    designOnly: {
      type: Boolean
    },
    inlineBearings: {
      type: Array,
      value: function () {
        return [];
      }
    },
    inlineIndex: {
      type: Number,
      value: 0
    },
    jobid: {
      type: String
    },
    jobStyles: Object,
    connections: {
      type: Object
    },
    otherAttributes: {
      type: Object
    },
    showLineToActive: {
      type: Boolean,
      notify: true
    },
    snapLength: {
      type: Number,
      notify: true
    },
    snapAngle: {
      type: Number,
      notify: true
    },
    latitude: {
      type: Number,
      notify: true
    },
    longitude: {
      type: Number,
      notify: true
    },
    modelDefaults: {
      type: Object,
      value: {}
    },
    nodes: {
      type: Object
    },
    modelConfig: Object,
    willRunCommand: {
      type: Boolean,
      notify: true
    },
    useMetricUnits: {
      type: Boolean,
      value: false
    },
    activeItemLocationUpdated: {
      type: Boolean,
      value: false,
      notify: true
    },
    enterNextPolylinePoint: {
      type: Object,
      notify: true
    },
    closestIncludesReferenceJobs: {
      type: Boolean,
      notify: true,
      computed: 'computeClosestIncludesReferenceJobs(inReferenceSelectMode)'
    },
    _inlineToggleButtonColor: {
      type: String,
      value: null
    },
    _bisectorToggleButtonColor: {
      type: String,
      value: null
    }
  },

  observers: [
    'positionChanged(latitude, longitude)',
    '_snappingLengthChanged(_snappingLength, lockedGeometryLength)',
    '_snappingAngleChanged(_snappingAngle, lockedGeometryAngle)',
    'updateShowDrawingGeometry(activeTool, activeCommand, gpsCircleConfig)'
  ],

  init: function (button) {
    this.inReferenceSelectMode = false;
    this.showLineToActive = false;
    if (this.enterNextPolylinePoint) {
      var quickEditItems = [];
      let button = this.activeButtonModel;
      let editNode = button.node.open_for_editing;
      let editConn = button.connection.open_for_editing;
      let node = this.nodes[this.activeItem.slice(1)];
      // pull up prompt AFTER completing polyline
      if (editNode || editConn) {
        if (editNode) {
          quickEditItems.push(this.activeItem);
        }
        if (editConn) {
          quickEditItems.push('c' + this.enterNextPolylinePoint.connId);
        }
        this.activeTool = 'editAttributes';
        this.activeCommand = 'edit';
        this.fire('run-command', {
          items: quickEditItems,
          callback: function (deleted) {
            if (deleted) {
              this.activeTool = 'drawOnMap';
              this.activeCommand = 'init';
              this.init();
            } else {
              var collect = this.designCollect(node, null, node.latitude, node.longitude, this.activeItem.slice(1));
              if (!collect) {
                this.activeTool = 'drawOnMap';
                this.activeCommand = 'init';
                this.init();
              }
            }
          }.bind(this),
          name: button.label
        });
      }

      let connId = SquashNulls(this.enterNextPolylinePoint, 'connId');
      var line = loadRenderMap.lineLocations[connId];
      if (line) line.setEditable(false);
      this.enterNextPolylinePoint = null;
    }
    if (SquashNulls(this.activeButtonModel, 'node', 'insert') === true) {
      this.activeCommand = 'insert';
      this.fire('prompt', { text: this.activeButtonModel.label });
      this.fire('cancel-secondary-prompt');
      this.fire('cancel-tertiary-prompt');
    } else if (this.activeButtonModel != null && this.activeButtonModel.view == 'assessment') {
      this.activeCommand = 'new';
      this.fire('prompt', { text: 'Insert & Assess (' + this.activeButtonModel.label + ')' });
      this.fire('cancel-secondary-prompt');
      this.fire('cancel-tertiary-prompt');
    } else if (this.activeButtonModel?.function == '_button_rotate_icon') {
      this.activeCommand = 'select';
      this.fire('prompt', { text: 'Select Node to Rotate' });
      this.fire('cancel-secondary-prompt');
      this.fire('cancel-tertiary-prompt');
    } else if (this.activeButtonModel != null && this.activeButtonModel.connection == null) {
      this.activeCommand = 'new';
      this.fire('prompt', { text: 'Place ' + this.activeButtonModel.label });
      this.fire('cancel-secondary-prompt');
      this.fire('cancel-tertiary-prompt');
    } else {
      this.activeCommand = 'select';
      if (this.activeButtonModel?.draw_join) {
        this.fire('prompt', { text: 'Draw New' });
        this.fire('cancel-secondary-prompt');
      } else {
        this.fire('prompt', { text: 'Select Starting Node' });
        if (SquashNulls(this.activeButtonModel, 'node', 'take_focus') === true) {
          this.fire('secondary-prompt', { text: 'Draw New' });
        } else {
          this.fire('cancel-secondary-prompt');
        }
      }
      this.fire('cancel-tertiary-prompt');
    }
    this.map_pan(this.latitude, this.longitude, this.domHost.findClosest());
  },

  /**
   * Map pan event handler (called from katapult-maps) when using the draw-on-map tool
   *
   * @param {number} latitude - latitude of the pointer
   * @param {number} longitude - longitude of the pointer
   * @param {Object} closest - closest item ({ item, distance })
   */
  map_pan: function (latitude, longitude, closest) {
    this.positionUpdated = true;

    // Check if we are trying to place a reference node and the closest item is a node in a reference job
    if (this.inReferenceSelectMode && this.closestIsNodeInReferenceJob(closest)) {
      let closestKey = closest.item.slice(1);

      // Used to track if we have a reference node currently highlighted
      this.referenceNodeHighlighted = closestKey;
      let marker = loadRenderMap?.pointLocations?.[closestKey];
      if (marker) this.fire('set-temp-highlight-location', { location: marker.data.l });

      return;
    }

    // Check if we have a reference node highlight and remove the highlight if so
    if (this.referenceNodeHighlighted) this.fire('set-temp-highlight-location', { location: null });

    // TODO (03-01-2022): This assessment logic is cobbled together... we'll want to do it differently on a file re-write
    let buttonIsAssessment = this.activeButtonModel?.view == 'assessment';
    let assessmentCanSelect = buttonIsAssessment && this.activeButtonModel?.assessment_permission != 'new_only';
    let assessmentCanCreate =
      buttonIsAssessment &&
      !['existing_only', 'existing_sections_only', 'existing_nodes_and_sections'].includes(this.activeButtonModel?.assessment_permission);

    // Normal selection logic
    if (this.activeCommand == 'select' || (this.activeCommand == 'new' && buttonIsAssessment && assessmentCanSelect)) {
      // Set activeItem if the closest item is an acceptable type
      if (this.activeButtonModel?.assessment_permission == 'existing_nodes_and_sections')
        this.activeItem = ['n', 's'].includes(closest?.item?.[0]) ? closest.item : '';
      else if (this.activeButtonModel?.assessment_permission == 'existing_sections_only')
        this.activeItem = closest?.item?.[0] == 's' ? closest.item : '';
      else this.activeItem = closest?.item?.[0] == 'n' ? closest.item : '';

      // If active command is new and the active button model is an assessment, fire the prompt
      if (this.activeCommand == 'new' && buttonIsAssessment) {
        let message = `${!assessmentCanCreate || this.activeItem ? '' : 'Insert & '}Assess (${this.activeButtonModel.label})`;
        this.fire('prompt', { text: message });
      }
    } else if (this.activeCommand == 'insert') {
      let closestConnections = this.getClosestConnections(closest, latitude, longitude);
      this.activeItem = closestConnections.length > 0 ? `c${closestConnections[0].connId}` : '';
    }
    // If we are using the polyline tool
    else if (this.activeButtonModel?.connection?.polyline) {
      // If the closest item is a node, and that node isn't the active item, keep track of it so we know where to end the polyline
      if (closest && closest.item[0] == 'n' && closest.item != this.activeItem) {
        this.endpoint = closest.item.slice(1);
        // If we haven't drawn any polyline points yet we can change the active command to a join
        if (!this.enterNextPolylinePoint) {
          this.activeCommand = 'join';
        }
        this.fire('prompt', { text: 'Select Endpoint' });
      } else {
        this.endpoint = null;
        this.activeCommand = 'add';
        this.fire('prompt', { text: 'Select Next Point' });
        this.fire('cancel-tertiary-prompt');
      }
    }
  },

  isReferenceButton: function () {
    // Check that we are adding an item and the node type is a reference
    const nodeType = this.activeButtonModel?.node?.attributes?.[this.modelDefaults.node_type_attribute]?.value ?? '';
    return this.modelDefaults.reference_node_types.includes(nodeType);
  },

  closestIsNodeInReferenceJob: function (closest) {
    if (closest && closest.item && closest.item[0] == 'n') {
      let closestKey = closest.item.slice(1);
      let pointLocationJobId = SquashNulls(loadRenderMap, 'pointLocations', closestKey, 'jobId');
      return pointLocationJobId && pointLocationJobId != this.jobid;
    }
  },

  computeClosestIncludesReferenceJobs: function (inReferenceSelectMode) {
    // Check if we are trying to place a reference node and if so, we should
    // include nodes in reference jobs when getting the closest items
    return inReferenceSelectMode && this.isReferenceButton();
  },

  insert: function (closestItem, latitude, longitude) {
    var closestConnections = this.getClosestConnections(closestItem, latitude, longitude);

    if (closestConnections.length) {
      let update = {};
      // Get current connection.
      let connId = closestConnections[0].connId;
      let conn = this.connections[connId];
      // Create new node and get back data and key
      let newNodeResults = this.getNodeObject(latitude, longitude, { update });
      let newNode = newNodeResults.data;
      let newNodeKey = newNodeResults.key;
      // Build a list of attributes to assign to the new connection (based off the existing connection's attributes)
      let connectionAttributes = [];
      for (let attribute in conn.attributes) {
        for (let attributeKey in conn.attributes[attribute]) {
          connectionAttributes.push({
            attribute,
            key: attributeKey,
            value: conn.attributes[attribute][attributeKey]
          });
        }
      }
      DataLayer.Connections.insertNode(
        connId,
        conn,
        this.nodes[conn.node_id_1],
        this.nodes[conn.node_id_2],
        newNodeKey,
        newNode,
        this.otherAttributes,
        FirebaseWorker,
        {
          update,
          method: 'mobile',
          attributes: connectionAttributes,
          jobStyles: this.jobStyles,
          connection_length_attributes: SquashNulls(this.modelConfig, 'connection_length_attributes')
        }
      );
      FirebaseWorker.ref('photoheight/jobs/' + this.jobid).update(update);
    }
    this.activeItem = '';
  },

  getClosestConnections: function (closestItem, latitude, longitude) {
    var closestConnections = [];
    var closestConnectionsList = [];
    if (closestItem != null && closestItem.item[0] == 'n') {
      var nodeId = closestItem.item.slice(1);
      var connections = SquashNulls(loadRenderMap.pointLocations[nodeId], 'data', 'n');
      for (var connId in connections) {
        let conn = SquashNulls(this.connections, connId);
        closestConnections.push({
          connId,
          distance: -1,
          n1: SquashNulls(this.nodes, conn.node_id_1),
          n2: SquashNulls(this.nodes, conn.node_id_2)
        });
        closestConnectionsList.push(connId);
        break;
      }
    }
    for (var connId in this.connections) {
      if (closestConnectionsList.indexOf(connId) == -1) {
        var n1 = SquashNulls(this.nodes, SquashNulls(this.connections, connId, 'node_id_1'));
        var n2 = SquashNulls(this.nodes, SquashNulls(this.connections, connId, 'node_id_2'));
        var distance = KatapultGeometry.CalcDistanceToLine(latitude, longitude, n1.latitude, n1.longitude, n2.latitude, n2.longitude);
        closestConnections.push({
          distance: distance,
          connId: connId,
          n1: n1,
          n2: n2
        });
        closestConnectionsList.push(connId);
      }
    }
    closestConnections.sort(function (a, b) {
      return a.distance - b.distance;
    });
    return closestConnections;
  },

  select: function (closestItem, latitude, longitude, detail) {
    this.callbackForAddCommand = detail?.callback;

    this.inReferenceSelectMode = false;
    if (closestItem == null || closestItem.item?.[0] != 'n') {
      // For the draw/join tool, add a new node if we are not close to an existing node
      if (this.activeButtonModel?.draw_join) {
        const editing = this.new();
        if (!editing) this.promptToSelectNext();
      } else this.fire('toast', 'Please select closer to an existing node.');
    } else {
      this.activeItem = closestItem.item;
      // If we are using the rotate icon tool
      if (this.activeButtonModel?.function == '_button_rotate_icon') {
        this.showLineToActive = true;
        this.activeCommand = 'rotateIcon';
        this._initialIconAngle = this.nodes[this.activeItem.slice(1)]?.style_adjustments?.rotation ?? 0;
        this.fire('prompt', { text: 'Set rotation' });
        this.fire('secondary-prompt', { text: 'Cancel' });
      } else if (
        this.activeButtonModel.connection != null &&
        (this.activeButtonModel.connection.location_1 == 'target' || this.activeButtonModel.connection.location_2 == 'target')
      ) {
        this.activeCommand = 'join';
        this.showLineToActive = true;
        this.fire('prompt', { text: 'Select Next Node to Join' });
        this.fire('secondary-prompt', { text: 'Cancel' });
      } else {
        // Check if we should go into reference selecting mode
        if (this.isReferenceButton()) {
          this.inReferenceSelectMode = true;
        }
        this.promptToSelectNext();
      }
    }
  },

  // Commit an icon rotation to the database
  rotateIcon: async function () {
    // Do nothing if we don't have a selected node
    const selectedNode = this.activeItem?.slice(1);
    if (!selectedNode) return;

    const update = {
      [`geohash/${selectedNode}/r`]: this._snappingAngle,
      [`nodes/${selectedNode}/style_adjustments/rotation`]: this._snappingAngle
    };
    await FirebaseWorker.ref(`photoheight/jobs/${this.jobid}`).update(update);

    // Reset the prompt
    this.init();
  },

  /**
   * Generates a new node object based on the active button model
   *
   * @param {number} latitude - latitude for the new node
   * @param {number} longitude - longitude for the new node
   * @param {Object} [options] - options object
   * @param {object} [options.update] - update object to add the new node to
   * @returns new node object
   */
  getNodeObject: function (latitude, longitude, options = {}) {
    // Get the active button so we know what attributes to add
    let button = this.activeButtonModel;
    // Build an attribute list to pass into the node
    const attributes = [];
    const buttonAttributes = button.node.attributes;
    const isAssessment = button.view === 'assessment';

    // Loop over each prop in the model
    for (const prop in buttonAttributes) {
      // Skip if the value is null
      if (buttonAttributes[prop].value == null) continue;

      const guiElement = this.otherAttributes?.[prop]?.gui_element;
      const key = guiElement === 'table' ? globalThis.FirebaseWorker.database().ref().push().key : 'button_added';

      // For assessments
      if (isAssessment) {
        const shouldStamp = buttonAttributes[prop].trigger == 'on_create' || buttonAttributes[prop].type == 'stamp_on_create';
        const attributeToStamp = buttonAttributes[prop].attribute;

        // Stamp an attribute if the button is an assessment and the attribute has an on_create trigger
        if (shouldStamp && attributeToStamp != null) {
          attributes.push({ attribute: buttonAttributes[prop].attribute, key, value: buttonAttributes[prop].value });
        }
        continue;
      }

      // Stamp an attribute if the button is NOT an assessment
      if (guiElement == 'multi_dropdown') {
        const values = buttonAttributes[prop].value ?? {};
        for (const [key, value] of Object.entries(values)) attributes.push({ attribute: prop, key, value });
        continue;
      }

      attributes.push({ attribute: prop, key, value: buttonAttributes[prop].value });
    }

    const createNodeOptions = {
      update: options.update,
      attributes: attributes,
      jobStyles: this.jobStyles,
      method: 'mobile',
      button: this.activeButton
    };
    return DataLayer.Nodes.create(latitude, longitude, this.otherAttributes, globalThis.FirebaseWorker, createNodeOptions);
  },

  add: function (closestItem, latitude, longitude) {
    // TODO (2024-11-14): This function should set `positionUpdated = false` at some point (new does, through promptToSelectNext)
    // Check if we are adding in a special reference and do that instead
    if (this.inReferenceSelectMode && this.closestIsNodeInReferenceJob(closestItem)) {
      let referenceNodeId = closestItem.item.slice(1);
      let referenceJobId = SquashNulls(loadRenderMap, 'pointLocations', referenceNodeId, 'jobId');
      let referenceNodeLocation = SquashNulls(loadRenderMap, 'pointLocations', referenceNodeId, 'data', 'l');
      let activeNodeId = this.activeItem.slice(1);
      let activeNodeLocation = [this.nodes[activeNodeId].latitude, this.nodes[activeNodeId].longitude];

      // Toggle off the reference layer so we don't get confusing visuals
      this.domHost.toggleReferenceJobLayer(referenceJobId, false);

      InsertJobReferenceConnection(
        this.jobid,
        activeNodeId,
        activeNodeLocation,
        referenceJobId,
        referenceNodeId,
        referenceNodeLocation,
        this.modelConfig,
        this.otherAttributes,
        this.jobStyles,
        this.jobCreator,
        this.activeButton,
        this.mappingButtons,
        FirebaseWorker
      ).then((results) => {
        if (results.success) this.fire('toast', results.message);
      });
      return;
    }

    // Otherwise, use normal add flow
    var button = this.activeButtonModel;
    // If the button draws a connection, make sure it has the data it needs
    if (button.connection != null) {
      if (this._snappingLength == 0) {
        this.fire('toast', 'Please enter or select a length for this item.');
        return;
      }
      if (!this.positionUpdated && !this.lockedGeometryAngle) {
        this.fire('toast', 'Please enter or select an angle for this item.');
        return;
      }
    }

    this.fire('highlight-conn', null);
    var editNode = false;
    var editConn = false;
    var update = {};

    // If drawing a polyline
    if (this.enterNextPolylinePoint) {
      var newEndpoint = [latitude, longitude];
      // If we are over a node, connect to that node
      if (this.endpoint) {
        let node = this.nodes[this.endpoint];
        newEndpoint = [node.latitude, node.longitude];
        update['geohash/' + this.endpoint + '/n/' + this.enterNextPolylinePoint.connId] = 1;
        update['connections/' + this.enterNextPolylinePoint.connId + '/node_id_1'] = this.endpoint;
        update['geohash/' + this.enterNextPolylinePoint.endpointNodeId] = null;
        update['nodes/' + this.enterNextPolylinePoint.endpointNodeId] = null;
      } else {
        update['geohash/' + this.enterNextPolylinePoint.endpointNodeId + '/l'] = newEndpoint;
        update['nodes/' + this.enterNextPolylinePoint.endpointNodeId + '/latitude'] = latitude;
        update['nodes/' + this.enterNextPolylinePoint.endpointNodeId + '/longitude'] = longitude;
      }

      var breakpoints = this.enterNextPolylinePoint.points.slice(0);
      update['geohash/' + this.enterNextPolylinePoint.connId + '~1/b'] = breakpoints;
      update['geohash/' + this.enterNextPolylinePoint.connId + '~2/b'] = breakpoints;
      update['connections/' + this.enterNextPolylinePoint.connId + '/breakpoints'] = breakpoints;
      update['geohash/' + this.enterNextPolylinePoint.connId + '~1/l'] = newEndpoint;
      update['geohash/' + this.enterNextPolylinePoint.connId + '~2/l2'] = newEndpoint;
      this.enterNextPolylinePoint.points.unshift(newEndpoint);
      var length = this.getConnPolylineLength(
        null,
        this.enterNextPolylinePoint.points.concat([this.enterNextPolylinePoint.firstPointLocation])
      );
      update['geohash/' + this.enterNextPolylinePoint.connId + '~1/d'] = length;
      update['geohash/' + this.enterNextPolylinePoint.connId + '~2/d'] = length;
      FirebaseWorker.ref('photoheight/jobs/' + this.jobid).update(update, (err) => {
        if (err) this.toast(err);
        this.activeItemLocationUpdated = !this.activeItemLocationUpdated;
      });
      // If we are over a node, and connected to that node, end the drawing
      if (this.endpoint) {
        this.endpoint = null;
        this.init();
      }
      return;
    }
    // For the draw/join tool, if it's near a node, join rather than adding a node
    if (this.activeButtonModel?.draw_join && closestItem?.item?.[0] == 'n') {
      this.join(closestItem, latitude, longitude);
      return;
    }
    // If drawing a plain old node
    else if (button.node != null && button.node.location == 'new') {
      const gpsLocation = this.getGPSLocationFromMoveTool();
      const latitudeToSet = gpsLocation?.latitude ?? latitude;
      const longitudeToSet = gpsLocation?.longitude ?? longitude;
      const nodeResults = this.getNodeObject(latitudeToSet, longitudeToSet, { update });

      var node = nodeResults.data;
      var new_node_id = nodeResults.key;
      editNode = button.node.open_for_editing;
      this.lastSelectedNode = this.activeItem.slice(1);
      if (button.node.take_focus || this.activeItem == '') {
        this.activeItem = 'n' + nodeResults.key;
      }
      // clear bisector and inline buttons
      this._bisectorToggleButtonColor = null;
      this._inlineToggleButtonColor = null;
    }

    // If drawing a connection
    if (button.connection != null && button.connection.location_1 == 'active' && button.connection.location_2 == 'new') {
      let lastNode = this.nodes[this.lastSelectedNode];
      var connData = this.createConnection(new_node_id, node, this.lastSelectedNode, lastNode, { update });
      editConn = connData.edit;
      if (button.polyline || button.connection.polyline) {
        this.enterNextPolylinePoint = {
          connId: connData.id,
          points: [[latitude, longitude]],
          endpointNodeId: new_node_id,
          firstPointLocation: [lastNode.latitude, lastNode.longitude]
        };
        loadRenderMap.setConnEditable = connData.id;
      }
    }

    // If there are pending updates to the job, write them now
    if (Object.keys(update).length) FirebaseWorker.ref('photoheight/jobs/' + this.jobid).update(update);

    this.snapLength = null;
    this.snapAngle = null;
    if (this.lockedGeometryLength) {
      this.lockedGeometryLength = false;
      this.$$('#lengthLock').classList.remove('showing');
    }
    if (this.lockedGeometryAngle) {
      this.lockedGeometryAngle = false;
      this.$$('#angleLock').classList.remove('showing');
    }

    // If there is a callback (e.g. from the assessment tool), run it and return
    if (typeof this.callbackForAddCommand === 'function') {
      this.callbackForAddCommand({ newNodeId: new_node_id });
      // Remove the callback
      this.callbackForAddCommand = null;
      return;
    }

    // If edit conn or edit node, open editor
    if ((editNode || editConn) && !button.polyline) {
      var quickEditItems = [];
      if (editNode) quickEditItems.push('n' + new_node_id);
      if (editConn) quickEditItems.push('c' + connData.id);

      // If the button is an assessment, us the assess attributes tool; otherwise, use edit attributes
      this.activeTool = button?.view === 'assessment' ? 'assessAttributes' : 'editAttributes';
      this.activeCommand = 'edit';

      this.fire('run-command', {
        items: quickEditItems,
        callback: function (deleted) {
          if (deleted) {
            this.activeTool = 'drawOnMap';
            this.activeCommand = 'init';
            this.init();
          } else {
            var collect = this.designCollect(node, connData, latitude, longitude, new_node_id);
            if (!collect) {
              this.activeTool = 'drawOnMap';
              this.activeCommand = 'init';
              this.init();
            }
          }
        }.bind(this),
        name: button.label
      });
    } else {
      this.designCollect(node, connData, latitude, longitude);
    }
  },

  new: function (closest, latitude, longitude) {
    var button = this.activeButtonModel;
    let buttonIsAssessment = button?.view == 'assessment';

    // Short-circuit if button node is null or location is not 'new'
    if (button.node == null || button.node.location != 'new') return;
    // Otherwise, get the model attributes
    let modelAttrs = button.node.attributes;

    // If the button is an assessment and it is for existing nodes but no node is selected, do nothing
    if (buttonIsAssessment && button.assessment_permission == 'existing_only' && !this.activeItem) {
      this.fire('toast', 'Please select closer to an existing node.');
      return;
    }
    // If the button is an assessment and it is for existing sections but no section is selected, do nothing
    else if (buttonIsAssessment && button.assessment_permission == 'existing_sections_only' && !this.activeItem) {
      this.fire('toast', 'Please select closer to an existing section.');
      return;
    }
    // If the button is an assessment and it is for existing sections but no section is selected, do nothing
    else if (buttonIsAssessment && button.assessment_permission == 'existing_nodes_and_sections' && !this.activeItem) {
      this.fire('toast', 'Please select closer to an existing node or section.');
      return;
    }
    // If the button is an assessment and is not for new nodes only and has an active item
    else if (buttonIsAssessment && button.assessment_permission != 'new_only' && this.activeItem) {
      let itemKey = '';
      let sectionKey = '';
      let connKey = '';
      let item = {};
      if (this.activeItem[0] == 'n') {
        // Using an assessment tool and selecting an existing node
        itemKey = this.activeItem.slice(1);
        item = this.get(`nodes.${itemKey}`);
      } else if (this.activeItem[0] == 's') {
        // Using an assessment tool and selecting an existing section
        const sectionConnectionKey = this.activeItem.slice(1);
        sectionKey = sectionConnectionKey.split(':')[1];
        connKey = sectionConnectionKey.split(':')[0];
        item = this.get(`connections.${connKey}.sections.${sectionKey}`);
        itemKey = `${connKey}/sections/${sectionKey}`;
      }

      // If the node exists
      if (item) {
        let attributes = [];
        let update = {};

        // Check for stamp_on_select attributes
        for (const prop in modelAttrs) {
          // If the attribute is on_select triggered
          if (modelAttrs[prop].trigger == 'on_select' || modelAttrs[prop].type == 'stamp_on_select') {
            // Skip this attribute if it is improperly configured
            if (!(modelAttrs[prop].value && modelAttrs[prop].attribute)) continue;
            // Skip this attribute if there is already a value for the given attribute and it should not be overwritten
            if (!modelAttrs[prop].overwrite && !!attributes[modelAttrs[prop].attribute]) continue;

            // Write the attribute to the node's attribute list
            let key = 'button_added';
            if (this.otherAttributes?.[prop]?.gui_element == 'table')
              key = FirebaseWorker.ref(`photoheight/jobs/${this.jobid}/${itemKey}`).push().key;
            attributes.push({ attribute: modelAttrs[prop].attribute, key, value: modelAttrs[prop].value });
          }
        }

        // Update firebase
        if (connKey) {
          DataLayer.Sections.setAttributes(connKey, sectionKey, item, attributes, this.otherAttributes, {
            update,
            jobStyles: this.jobStyles,
            method: 'mobile'
          });
        } else {
          DataLayer.Nodes.setAttributes(itemKey, item, attributes, this.otherAttributes, {
            update,
            jobStyles: this.jobStyles,
            method: 'mobile'
          });
        }
        globalThis.FirebaseWorker.database().ref(`photoheight/jobs/${this.jobid}`).update(update);
      }
    }
    // Otherwise, place a new node
    else {
      let update = {};
      let nodeResults = this.getNodeObject(latitude || this.latitude, longitude || this.longitude, { update });
      var new_node_id = nodeResults.key;
      this.lastSelectedNode = this.activeItem.slice(1);

      globalThis.FirebaseWorker.database().ref(`photoheight/jobs/${this.jobid}`).update(update);
      new_node_id = 'n' + new_node_id;
      if (button.node.take_focus || this.activeItem == '') this.activeItem = new_node_id;
    }

    if (button.node.open_for_editing) {
      var previousCommand = this.activeCommand;
      var quickEditItems = [];
      let nodeToOpen = new_node_id || this.activeItem;
      quickEditItems.push(nodeToOpen);

      this.activeTool = button?.view === 'assessment' ? 'assessAttributes' : 'editAttributes';
      this.activeCommand = 'edit';

      this.fire('run-command', {
        items: quickEditItems,
        callback: function (previousCommand, deleted) {
          // Check if the assessment button should also draw a new connection or join connection
          let assessmentDrawsConnection = button?.view == 'assessment' && button?.connection?.location_2 == 'new';
          let assessmentJoinsConnection = button?.view == 'assessment' && button?.connection?.location_2 == 'target';

          // If is assessment and should join connections
          if (assessmentJoinsConnection) {
            this.activeTool = 'drawOnMap';
            this.activeCommand = 'join';
            this.showLineToActive = true;
            this.fire('prompt', { text: 'Select Next Node to Join' });
            this.fire('secondary-prompt', { text: 'Cancel' });
          }
          // If previous command was select or is assessment and should draw connection
          else if (previousCommand == 'select' || assessmentDrawsConnection) {
            this.activeTool = 'drawOnMap';
            this.promptToSelectNext();
          }
          // Otherwise (not an assessment and was not previously selecting)
          else {
            this.activeTool = 'drawOnMap';
            this.activeCommand = 'init';
            this.init();
          }
        }.bind(this, previousCommand),
        name: button.label
      });

      return true;
    }
  },

  promptToSelectNext: function () {
    if (this.activeButton == 'anchor') {
      this.bisectorIndex = 0;
      this.inlineIndex = 0;
    }
    this._snappingLength = 0;
    this._snappingAngle = 0;
    this.positionUpdated = false;
    this.activeCommand = 'add';
    this.showLineToActive = true;
    if (this.activeButtonModel?.draw_join) {
      this.fire('prompt', { text: 'Join/Draw New' });
      this.fire('tertiary-prompt', { text: 'Mark Done', buttonColor: '#ff8300' });
    } else {
      this.fire('prompt', { text: 'Select Next Point' });
      this.fire('cancel-tertiary-prompt');
    }
    this.fire('secondary-prompt', { text: 'Cancel' });
  },

  join: function (closestItem, latitude, longitude) {
    if (closestItem == null || closestItem.item[0] != 'n') this.fire('toast', 'Please select closer to an existing node.');
    else {
      this.lastSelectedNode = this.activeItem.slice(1);
      var new_node_id = closestItem.item.slice(1);

      var newNodeGeoData = SquashNulls(loadRenderMap.pointLocations[new_node_id], 'data');
      var lastSelectedNodeGeoData = SquashNulls(this.geohash, this.lastSelectedNode);
      var alreadyConnected = false;
      for (var connId in lastSelectedNodeGeoData.n) {
        if (SquashNulls(newNodeGeoData.n, connId) != '') {
          alreadyConnected = true;
          break;
        }
      }
      if (alreadyConnected) {
        this.fire('confirm', {
          heading: 'Items already connected.',
          body: 'These items are already connected, are you sure you want to create another connection?',
          affirmativeText: 'Create',
          dismissiveText: 'Cancel',
          affirmativeStyle: 'background-color:var(--secondary-color); color:var(--secondary-color-text-color);',
          callback: () => {
            this.commitJoin(latitude, longitude, new_node_id);
          }
        });
      } else {
        this.commitJoin(latitude, longitude, new_node_id);
      }
    }
  },

  commitJoin: function (latitude, longitude, new_node_id) {
    var lastNode = this.nodes[this.lastSelectedNode];
    var newNode = this.nodes[new_node_id];
    let update = {};
    var connData = this.createConnection(new_node_id, newNode, this.lastSelectedNode, lastNode, { update });
    if (this.jobid && Object.keys(update).length) {
      FirebaseWorker.ref(`photoheight/jobs/${this.jobid}`).update(update);
    }
    if (connData.edit) {
      this.activeTool = 'editAttributes';
      this.activeCommand = 'edit';
      this.activeItem = 'c' + connData.id;
      this.fire('run-command', {
        items: ['c' + connData.id],
        callback: function (deleted) {
          if (deleted) {
            this.activeTool = 'drawOnMap';
            this.activeCommand = 'init';
            this.init();
          } else {
            var collect = this.designCollect(null, connData, latitude, longitude, new_node_id);
            if (!collect) {
              this.activeTool = 'drawOnMap';
              this.activeCommand = 'init';
              this.init();
            }
          }
        }.bind(this),
        name: 'Connection'
      });
    } else this.designCollect(null, connData, latitude, longitude, new_node_id);
  },

  getConnPolylineLength: function (path, points) {
    if (!path) {
      for (var i = 0; i < points.length; i++) {
        points[i] = new google.maps.LatLng(points[i][0], points[i][1]);
      }
      path = new google.maps.MVCArray(points);
    }
    return Round(google.maps.geometry.spherical.computeLength(path) / 0.3048, 1);
  },

  designCollect: function (node, connData, latitude, longitude, new_node_id) {
    if (new_node_id != null) node = this.nodes[new_node_id];
    if (node != null && !this.designOnly) {
      if (connData != null) {
        let conn_type = connData.edit
          ? PickAnAttribute(this.connections?.[connData.id]?.['attributes'], this.modelDefaults.connection_type_attribute)
          : connData.conn_type;
        if (this.collectionOrderLogic.connection_types_to_skip.indexOf(conn_type) == -1) {
          this.activeItem = 'c' + connData.id;
          this.activeButton = null;
          this.willRunCommand = true;
          this.activeTool = 'jobTimeCollection';
          this.activeCommand = 'change';
          this.fire('zoom-to-active');
          this.fire('run-command');
          return true;
        }
      }
      const nodeType = PickAnAttribute(node.attributes, this.modelDefaults.node_type_attribute);
      if (this.collectionOrderLogic.node_types_to_skip.indexOf(nodeType) == -1) {
        this.activeItem = 'n' + new_node_id;
        this.activeButton = null;
        this.willRunCommand = true;
        this.activeTool = 'jobTimeCollection';
        this.activeCommand = 'change';
        this.fire('zoom-to-active');
        this.fire('run-command');
        return true;
      }
    } else if (new_node_id != null) {
      this.activeItem = 'n' + new_node_id;
    }
  },

  createConnection: function (node1Key, node1, node2Key, node2, options) {
    let edit = false;
    options = options || {};
    let button = options.buttonModel || this.activeButtonModel;
    if (button.connection != null) {
      // Create an attributes list for the connection
      let attributes = [];
      for (var property in button.connection.attributes) {
        if (button.connection.attributes[property].value != null) {
          let key = 'button_added';
          if (SquashNulls(this.otherAttributes, property, 'gui_element') == 'table')
            key = FirebaseWorker.ref(`photoheight/jobs/${this.jobId}/nodes`).push().key;
          attributes.push({
            attribute: property,
            key,
            value: button.connection.attributes[property].value
          });
        }
      }
      // Create the connection
      var connectionResults = DataLayer.Connections.create(node1Key, node1, node2Key, node2, this.otherAttributes, FirebaseWorker, {
        jobStyles: this.jobStyles,
        method: 'mobile',
        button: this.activeButton,
        attributes,
        key: options.connId,
        connection_length_attributes: SquashNulls(this.modelConfig, 'connection_length_attributes'),
        update: options.update
      });
      edit = button.connection.open_for_editing;
    }
    return {
      edit: edit,
      id: connectionResults.key,
      conn_type: connectionResults.data.attributes?.[this.modelDefaults.connection_type_attribute]
    };
  },

  action_secondary: function () {
    if (this.activeCommand == 'select') {
      var editing = this.new();
      if (!editing) {
        this.promptToSelectNext();
      }
    } else if (this.activeCommand == 'rotateIcon') {
      this._snappingAngle = this._initialIconAngle;
      this._rotateIcon();
      this.init();
    } else {
      if (this.activeButtonModel?.draw_join) this.activeItem = '';
      this.init();
    }
  },

  action_tertiary: function () {
    // For the draw/join tool, the tertiary button will mark a node done
    if (this.activeButtonModel?.draw_join) {
      this.dispatchEvent(new CustomEvent('done-collection', { detail: { drawJoin: true } }));
    }
  },

  positionChanged: function () {
    if (this.activeTool == 'drawOnMap' && (this.activeCommand == 'add' || this.activeCommand == 'rotateIcon')) {
      var activeNode = SquashNulls(this.nodes, this.activeItem.slice(1));
      if (activeNode) {
        if (!this.lockedGeometryLength) {
          var length =
            google.maps.geometry.spherical.computeDistanceBetween(
              new google.maps.LatLng(activeNode.latitude, activeNode.longitude),
              new google.maps.LatLng(this.latitude, this.longitude)
            ) / (this.useMetricUnits ? 1 : 0.3048);
          if (length < 10) length = Round(length, 2);
          else if (length < 100) length = Round(length, 1);
          else length = Math.round(length);
          this._snappingLength = length;
        }
        if (!this.lockedGeometryAngle) {
          let ang = google.maps.geometry.spherical.computeHeading(
            new google.maps.LatLng(activeNode.latitude, activeNode.longitude),
            new google.maps.LatLng(this.latitude, this.longitude)
          );
          this._snappingAngle = Round((ang + 360) % 360, 1);
        }
      }
    }
  },

  _snappingLengthChanged: function () {
    if (this.lockedGeometryLength) {
      var center = SquashNulls(this.nodes, this.activeItem.slice(1));
      this.snapLength = {
        centerLat: center.latitude,
        centerLon: center.longitude,
        length: (this.useMetricUnits ? 1 : 0.3048) * this._snappingLength
      };
    } else this.snapLength = null;
  },

  _snappingAngleChanged: function () {
    if (this.lockedGeometryAngle) {
      var center = SquashNulls(this.nodes, this.activeItem.slice(1));
      this.snapAngle = {
        centerLat: center.latitude,
        centerLon: center.longitude,
        angle: this._snappingAngle
      };
    } else this.snapAngle = null;

    // If we are using the rotate icon tool, update the icon angle
    if (this.activeCommand == 'rotateIcon') {
      if (!this._rotateIconThrottled) this._rotateIconThrottled = Throttle(this._rotateIcon, 250);
      this._rotateIconThrottled();
    }
  },

  toggleBisectorInline: function (e) {
    // if you click the bisector button and it has already been selected
    if (e.currentTarget.id == 'bisectorIcon' && this._bisectorToggleButtonColor == '#ffb01f') {
      if (this.bisectorBearings.length > 0) {
        this.bisectorIndex++;
        if (this.bisectorIndex > this.bisectorBearings.length - 1) this.bisectorIndex = 0;
        this._snappingAngle = this.bisectorBearings[this.bisectorIndex].angle;
        this.fire('highlight-conn', this.bisectorBearings[this.bisectorIndex].conn);
      }
    } else if (e.currentTarget.id == 'bisectorIcon') {
      // if you click the bisector button for the first time
      // deselect the inline button
      this._inlineToggleButtonColor = null;
      // select the bisector button
      this._bisectorToggleButtonColor = '#ffb01f';
      // calc the bisector bearing options for the currently selected node
      this.bisectorBearings = this.getSnapConnectionBearings('bisector', this.activeItem);
      if (this.bisectorBearings.length > 0) {
        this.lockedGeometryAngle = true;
        this._snappingAngle = this.bisectorBearings[this.bisectorIndex].angle;
        this.fire('highlight-conn', this.bisectorBearings[this.bisectorIndex].conn);
      }
    } else if (this._inlineToggleButtonColor == '#ffb01f') {
      // if you click the inline button and it has already been selected
      if (this.inlineBearings.length > 0) {
        this.inlineIndex++;
        if (this.inlineIndex > this.inlineBearings.length - 1) this.inlineIndex = 0;
        this._snappingAngle = this.inlineBearings[this.inlineIndex].angle;
        this.fire('highlight-conn', this.inlineBearings[this.inlineIndex].conn);
      }
    } else {
      // if you click the inline button for the first time
      // deselect the bisector button
      this._bisectorToggleButtonColor = null;
      // select the inline button
      this._inlineToggleButtonColor = '#ffb01f';
      // calc the inline bearing options for the currently selected node
      this.inlineBearings = this.getSnapConnectionBearings('inline', this.activeItem);
      if (this.inlineBearings.length > 0) {
        this.lockedGeometryAngle = true;
        this._snappingAngle = this.inlineBearings[this.inlineIndex].angle;
        this.fire('highlight-conn', this.inlineBearings[this.inlineIndex].conn);
      }
    }
    // Show the angle lock
    this.shadowRoot.querySelector('#angleLock').classList.add('showing');
  },

  getSnapConnectionBearings: function (type, startingNode) {
    var bearings = [];
    var nodeId = startingNode.slice(1);
    var node = SquashNulls(this.nodes, nodeId);
    var linkedConnections = [];
    for (let connId in this.connections) {
      var conn_type = PickAnAttribute(this.connections[connId].attributes, this.modelDefaults.connection_type_attribute);
      if (
        (conn_type == 'aerial cable' ||
          conn_type == 'overlash' ||
          conn_type == 'slack span' ||
          conn_type == 'overhead guy' ||
          conn_type == 'pole to pole guy' ||
          this.modelDefaults.reference_connection_types.includes(conn_type)) &&
        (this.connections[connId].node_id_1 == nodeId || this.connections[connId].node_id_2 == nodeId)
      )
        linkedConnections.push(this.connections[connId]);
    }
    if (type == 'inline') {
      for (let j = 0; j < linkedConnections.length; j++) {
        let otherNode, nodeType, theta;
        if (linkedConnections[j].node_id_1 == nodeId) otherNode = SquashNulls(this.nodes, linkedConnections[j].node_id_2);
        else otherNode = SquashNulls(this.nodes, linkedConnections[j].node_id_1);
        nodeType = PickAnAttribute(otherNode?.attributes, this.modelDefaults.node_type_attribute);
        if (!this.modelDefaults.anchor_node_types.includes(nodeType)) {
          let p1 = new google.maps.LatLng(node.latitude, node.longitude),
            p2 = new google.maps.LatLng(otherNode.latitude, otherNode.longitude);
          theta = google.maps.geometry.spherical.computeHeading(p1, p2);
          theta = (theta + 360) % 360;
          bearings.push({ angle: Round(theta, 1), conn: { p1, p2 }, opp: false });
          if (theta < 180) bearings.push({ angle: Round(theta + 180, 1), conn: { p1, p2 }, opp: true });
          else bearings.push({ angle: Round(theta - 180, 1), conn: { p1, p2 }, opp: true });
        }
      }
      bearings.sort((a, b) => a.angle - b.angle);
      bearings.sort((a, b) => {
        if (!a.opp && b.opp) {
          return 1;
        } else if (a.opp && !b.opp) {
          return -1;
        } else {
          return 0;
        }
      });
      return bearings;
    } else if (type == 'bisector') {
      var connectionBearings = [];
      for (let j = 0; j < linkedConnections.length; j++) {
        let otherNode, nodeType;
        if (linkedConnections[j].node_id_1 == nodeId) otherNode = SquashNulls(this.nodes, linkedConnections[j].node_id_2);
        else otherNode = SquashNulls(this.nodes, linkedConnections[j].node_id_1);
        nodeType = PickAnAttribute(otherNode?.attributes, this.modelDefaults.node_type_attribute);
        if (!this.modelDefaults.anchor_node_types.includes(nodeType)) {
          let p1 = new google.maps.LatLng(node.latitude, node.longitude),
            p2 = new google.maps.LatLng(otherNode.latitude, otherNode.longitude);
          connectionBearings.push({ angle: google.maps.geometry.spherical.computeHeading(p1, p2), conn: { p1, p2 } });
        }
      }
      for (let i = 0; i < connectionBearings.length; i++) {
        for (let j = i + 1; j < connectionBearings.length; j++) {
          let theta = (connectionBearings[i].angle + connectionBearings[j].angle) / 2;
          theta = (theta + 360) % 360;
          let conn1 = connectionBearings[i].conn,
            conn2 = connectionBearings[j].conn;
          let diff1 = Math.abs(connectionBearings[i].angle - connectionBearings[j].angle);
          let diff2 = 360 - diff1;
          bearings.push({ angle: Round(theta, 1), conn: [conn1, conn2], diff: diff1 });
          if (theta < 180) bearings.push({ angle: Round(theta + 180, 1), conn: [conn1, conn2], diff: diff2 });
          else bearings.push({ angle: Round(theta - 180, 1), conn: [conn1, conn2], diff: diff2 });
        }
      }
      bearings.sort((a, b) => b.diff - a.diff);
      return bearings;
    }
  },

  lockGeometry: function (e) {
    const { currentTarget: input } = e ?? {};
    this.fire('highlight-conn', null);
    this._bisectorToggleButtonColor = null;
    this._inlineToggleButtonColor = null;
    if (input.id == 'inputLength') this.lockedGeometryLength = true;
    else if (input.id == 'inputAngle') this.lockedGeometryAngle = true;
  },

  unlockGeometry: function (e) {
    // Unlock the length or angle, depending on the button.
    // If no button is specified, unlock both.
    const { currentTarget: button } = e ?? {};

    this.fire('highlight-conn', null);

    if (button == null || button.id == 'lengthLock') {
      this.lockedGeometryLength = false;
    }

    if (button == null || button.id == 'angleLock') {
      this.lockedGeometryAngle = false;
      this._bisectorToggleButtonColor = null;
      this._inlineToggleButtonColor = null;
    }
  },

  showOrHideGPSActions: function () {
    this.domHost.$.moveItem.toggleGPSActions();
  },

  getGPSLocationFromMoveTool: function () {
    return this.domHost.$.moveItem.getGPSLocation();
  },

  updateShowDrawingGeometry: function () {
    const { activeTool, activeCommand, gpsCircleConfig } = this;
    const { visible: gpsCircleVisible } = gpsCircleConfig ?? {};

    const showDrawingGeometryInputs = activeTool == 'drawOnMap' && (activeCommand == 'add' || activeCommand == 'rotateIcon');
    const showDrawingGeometry = showDrawingGeometryInputs || gpsCircleVisible;
    Object.assign(this, { showDrawingGeometryInputs, showDrawingGeometry });
  },

  // Copied from desktop
  _rotateIcon: async function () {
    // Do nothing if we are not in the rotate icon command
    const selectedNode = this.activeItem.slice(1);
    if (this.activeCommand != 'rotateIcon' || !selectedNode) return;

    // Load the GeoStyleToIcon module
    const { GeoStyleToIcon } = await import('../../modules/GeoStyleToIcon.js');

    // Locally update the icon rotation, depending on the item type
    const selectedMapMarker = loadRenderMap.pointLocations?.[selectedNode];
    if (selectedMapMarker) selectedMapMarker.icon = GeoStyleToIcon({ ...selectedMapMarker.data, r: this._snappingAngle });
  },

  getByKey: function (type, id) {
    return this.domHost.$[type].child(id);
  },

  activeButtonChanged: function () {
    this.init();
  },

  activeCommandChanged: function () {
    this.fire('highlight-conn', null);
    this.lockedGeometryAngle = false;
    this.lockedGeometryLength = false;
    this.unlockGeometry();
    this._bisectorToggleButtonColor = null;
    this._inlineToggleButtonColor = null;
  },

  activeItemChanged: function () {
    // For the draw/join tool, show different prompts when hovering over a node
    if (this.activeButtonModel?.draw_join && this.activeCommand == 'select') {
      if (this.activeItem?.[0] == 'n') {
        this.fire('prompt', { text: 'Select Starting Node' });
        this.fire('cancel-secondary-prompt');
        this.fire('tertiary-prompt', { text: 'Mark Done', buttonColor: '#ff8300' });
      } else {
        this.fire('prompt', { text: 'Draw New' });
        this.fire('cancel-secondary-prompt');
        this.fire('cancel-tertiary-prompt');
      }
    }
  },

  formatGpsAccuracy: function () {
    const { gpsCircleConfig, useMetricUnits } = this;
    const { accuracy } = gpsCircleConfig ?? {};
    const formattedAccuracy = useMetricUnits ? `${Math.round(accuracy)}m` : `${Math.round(Convert(accuracy, 'm', 'ft'))}ft`;
    return formattedAccuracy;
  }
});
